import { isEmpty } from 'lodash';
import { DateTime } from 'luxon';
import { Component, FormEvent } from 'react';
import { WrappedFieldProps } from 'redux-form';
import styled from 'styled-components';

import FormControlLabel from 'dashboard/components/FormControls/FormControlLabel';
import FormError from 'dashboard/components/FormControls/FormError';
import ReactSelect from 'dashboard/components/ReactSelect';
import FormGroup from 'dashboard/layouts/FormLayout/FormGroup';
import FormSection from 'dashboard/layouts/FormLayout/Section';
import Input from 'design-system/components/Input';
import { InputSizes } from 'design-system/mixins/input';
import FormControlProps from 'lib/interfaces/FormControlProps';
import { EMPTY_DATE } from 'lib/utils/formValidation/isRequiredDate';

// Luxon dates are 1-indexed as opposed to Moment, which is 0-indexed
const MONTH_OPTIONS = [
  { label: 'January', value: '1' },
  { label: 'February', value: '2' },
  { label: 'March', value: '3' },
  { label: 'April', value: '4' },
  { label: 'May', value: '5' },
  { label: 'June', value: '6' },
  { label: 'July', value: '7' },
  { label: 'August', value: '8' },
  { label: 'September', value: '9' },
  { label: 'October', value: '10' },
  { label: 'November', value: '11' },
  { label: 'December', value: '12' },
];

const MonthSelect = styled(ReactSelect<string>)`
  grid-column: span 2;
`;

interface DateInputFieldProps extends FormControlProps {
  className?: string;
  id?: string;
  label?: string;
  disabled?: boolean;
  yearMin?: number;
  yearMax?: number;
  size?: Exclude<InputSizes, 'lg'>;
}

interface DeserializedDate {
  year: string;
  month: string;
  day: string;
}

type DateInputFieldState = DeserializedDate;

/**
 * `props.value` should be a YYYY-MM-DD formatted string, NOT a timestamp. If a
 * timestamp is passed, timezone offsets can cause an off-by-one error for the
 * displayed date.
 */
class DateInputField extends Component<
  DateInputFieldProps & WrappedFieldProps,
  DateInputFieldState
> {
  static defaultProps = {
    yearMin: 1900,
    yearMax: DateTime.local().year,
  };

  constructor(props: DateInputFieldProps & WrappedFieldProps) {
    super(props);

    this.state = {
      day: '',
      month: '',
      year: '',
    };
  }

  /**
   * When date is incomplete, it must be stored in state since value is full date string.
   */
  private get deserializedValue(): DeserializedDate {
    if (!/\d{4}-\d{2}-\d{2}/.test(this.props.input.value)) {
      return this.state;
    } else {
      // Use the Luxon Date Indexing to get the year, month, and day
      const date = DateTime.fromISO(this.props.input.value);

      return {
        year: String(date.year),
        month: String(date.month),
        day: String(date.day),
      };
    }
  }

  public render() {
    const {
      className,
      id,
      label,
      disabled,
      input,
      yearMin,
      yearMax,
      size,
      meta: { error, submitFailed, touched },
    } = this.props;

    const hasError = !!(error && (submitFailed || touched));

    const commonProps = {
      size,
      disabled,
      hasError,
    };

    return (
      <FormGroup id={id} className={className}>
        {label && <FormControlLabel htmlFor={`${id}--month`}>{label}</FormControlLabel>}
        <FormSection cols={4}>
          <MonthSelect
            {...commonProps}
            id={`${id}--month`}
            name={`${input.name}-month`}
            value={this.deserializedValue.month}
            placeholder='Month'
            onChange={this.handleChangeMonth}
            data-test={`${input.name}-month`}
            options={MONTH_OPTIONS}
            isClearable={true}
            portal={true}
          />
          <Input
            {...commonProps}
            id={`${id}--day`}
            name={`${input.name}-day`}
            data-test={`${input.name}-day`}
            min={0}
            max={31}
            onChange={this.handleChangeDay}
            placeholder='Day'
            type='number'
            // Default to empty string so it starts as a controlled input
            value={this.deserializedValue.day}
          />
          <Input
            {...commonProps}
            id={`${id}--year`}
            name={`${input.name}-year`}
            data-test={`${input.name}-year`}
            min={yearMin}
            max={yearMax}
            onChange={this.handleChangeYear}
            placeholder='Year'
            type='number'
            // Default to empty string so it starts as a controlled input
            value={this.deserializedValue.year}
          />
        </FormSection>
        {hasError && <FormError>{error}</FormError>}
      </FormGroup>
    );
  }

  private handleChangeMonth = (month: string) => {
    const newValue = {
      ...this.deserializedValue,
      month: month,
    };

    this.propagateDateChange(newValue);
  };

  private handleChangeDay = (event: FormEvent<HTMLInputElement>) => {
    const newValue = {
      ...this.deserializedValue,
      day: (event.target as HTMLInputElement).value,
    };

    this.propagateDateChange(newValue);
  };

  private handleChangeYear = (event: FormEvent<HTMLInputElement>) => {
    const newValue = {
      ...this.deserializedValue,
      year: (event.target as HTMLInputElement).value,
    };

    this.propagateDateChange(newValue);
  };

  private propagateDateChange(newValue: DeserializedDate) {
    this.setState(newValue);

    const newYear = parseInt(newValue.year, 10);
    const newMonth = parseInt(newValue.month, 10);
    const newDay = parseInt(newValue.day, 10);

    const dateValue = DateTime.local(newYear, newMonth, newDay);

    // Requires that this is above the minYear as configured in defaultProps
    if (dateValue.isValid && dateValue > DateTime.local(1900, 1, 1)) {
      this.props.input.onChange(dateValue.toISO());
    } else if (isEmpty(dateValue.day) && isEmpty(dateValue.month) && isEmpty(dateValue.year)) {
      // Must be '', null or undefined doesn't update the form value
      this.props.input.onChange(EMPTY_DATE);
    } else {
      // Partial dates should get passed through so validations
      // can handle appropriately
      this.props.input.onChange(dateValue);
    }
  }
}

export default DateInputField;
